import { useBuscaDoMomento } from '../../layout/tela-dividida';
import { memo, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { sugerirCodigoHierarquico } from '@malgoos/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Archive, ArrowDown, ArrowUp, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, CornerDownRight, Pencil, Plus, Search, Trash2, Upload } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Segmentado, Selo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Entrada, Formulario, Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { useDebounce } from '../../componentes/tabela';
import { buscaDaUrl, comBuscaNaUrl } from '../../componentes/filtros-visao';
import { BotaoModeloCsv, DialogoImportacao } from '../../componentes/importacao';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post } from '../../nucleo/api';
import { numero } from '../../nucleo/formato';
import { posicaoNaPagina, recuperarFoco } from '../../nucleo/foco';
import { useSessao } from '../../nucleo/sessao';
import { caminhoDaCategoria, useCategorias } from './dados';
import { GRUPOS_DRE, invalidarFinanceiro, NATUREZAS, PAPEIS_DO_TIPO, type Categoria, type TipoCategoria } from './tipos';
import { rotuloCategoria } from '../../nucleo/rotulos';

interface No extends Categoria {
  filhas: No[];
}

function montarArvore(itens: Categoria[], tipo: TipoCategoria): No[] {
  const nos = new Map(itens.filter((c) => c.tipo === tipo).map((c) => [c.id, { ...c, filhas: [] as No[] }]));
  const raizes: No[] = [];
  for (const n of nos.values()) {
    const pai = n.parent_id ? nos.get(n.parent_id) : undefined;
    if (pai) pai.filhas.push(n);
    else raizes.push(n);
  }
  return raizes;
}

/** B5: ordem do antigo, pelo código (2 antes de 10) e, sem código, pelo nome. */
const porCodigo = (a: Categoria, b: Categoria) => (a.codigo ?? '').localeCompare(b.codigo ?? '', undefined, { numeric: true }) || a.nome.localeCompare(b.nome);

/** B5 (modo Árvore, como no antigo): receitas e despesas numa árvore só, cada nível ordenado pelo código. */
export function montarArvoreUnica<C extends Categoria>(itens: C[]): (C & { filhas: (C & { filhas: unknown[] })[] })[] {
  type N = C & { filhas: N[] };
  const nos = new Map<string, N>(itens.map((c) => [c.id, { ...c, filhas: [] as N[] }]));
  const raizes: N[] = [];
  for (const n of nos.values()) {
    const pai = n.parent_id ? nos.get(n.parent_id) : undefined;
    if (pai) pai.filhas.push(n);
    else raizes.push(n);
  }
  const ordenar = (lista: N[]) => {
    lista.sort(porCodigo);
    for (const n of lista) ordenar(n.filhas);
  };
  ordenar(raizes);
  return raizes as never;
}

/** B5: linhas à vista da Árvore: a raiz sempre; a filha quando todos os pais estão abertos (ou `tudo`, na busca). */
export function linhasDaArvore<T extends { id: string; filhas: T[] }>(raizes: readonly T[], abertos: ReadonlySet<string>, tudo = false): { no: T; nivel: number }[] {
  const saida: { no: T; nivel: number }[] = [];
  const andar = (nos: readonly T[], nivel: number) => {
    for (const no of nos) {
      saida.push({ no, nivel });
      if (no.filhas.length && (tudo || abertos.has(no.id))) andar(no.filhas, nivel + 1);
    }
  };
  andar(raizes, 0);
  return saida;
}

/** B5: ids com filhas (o "Expandir tudo"). */
export function idsComFilhas<T extends { id: string; filhas: T[] }>(raizes: readonly T[]): Set<string> {
  const ids = new Set<string>();
  const andar = (nos: readonly T[]) => {
    for (const n of nos) {
      if (n.filhas.length) {
        ids.add(n.id);
        andar(n.filhas);
      }
    }
  };
  andar(raizes);
  return ids;
}

/** B5: todos os nós da árvore (podada), em profundidade: a Lista mostra o que a busca deixou. */
export function nosDaArvore<T extends { filhas: T[] }>(raizes: readonly T[]): T[] {
  const saida: T[] = [];
  const andar = (nos: readonly T[]) => {
    for (const n of nos) {
      saida.push(n);
      andar(n.filhas);
    }
  };
  andar(raizes);
  return saida;
}

/** B5: modos do Plano de contas. Quadro (o de hoje) é o padrão. */
export const VISOES_PLANO = ['quadro', 'arvore', 'lista'] as const;
export type VisaoPlano = (typeof VISOES_PLANO)[number];
const CHAVE_VISAO_PLANO = 'malgoos.planoContas.visao';

/** B5: o modo da URL (`?visao=`) vale mais; sem ele, o lembrado no aparelho; senão, o Quadro. */
export function visaoDoPlano(daUrl: string | null, guardada: string | null): VisaoPlano {
  const aceita = (v: string | null): v is VisaoPlano => !!v && (VISOES_PLANO as readonly string[]).includes(v);
  return aceita(daUrl) ? daUrl : aceita(guardada) ? guardada : 'quadro';
}

function lerVisaoGuardada(): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(CHAVE_VISAO_PLANO);
  } catch {
    return null;
  }
}

function guardarVisao(v: VisaoPlano) {
  try {
    localStorage.setItem(CHAVE_VISAO_PLANO, v);
  } catch {
    // Sem armazenamento (janela privada): o modo fica só na URL.
  }
}

/** B5: o modo do Plano de contas na URL (`?visao=`, replace; o Quadro não vai) e no aparelho. */
function useVisaoPlano(): [VisaoPlano, (v: VisaoPlano) => void] {
  const [url, setUrl] = useSearchParams();
  const visao = visaoDoPlano(url.get('visao'), lerVisaoGuardada());
  const mudar = (v: VisaoPlano) => {
    guardarVisao(v);
    setUrl(
      (atual) => {
        const n = new URLSearchParams(atual);
        if (v === 'quadro') n.delete('visao');
        else n.set('visao', v);
        return n;
      },
      { replace: true },
    );
  };
  return [visao, mudar];
}

/** Texto para comparar na busca: sem acento e sem diferença de maiúsculas. */
const comparavel = (v: string) =>
  v
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .toLowerCase();

/**
 * Fase 2 (W36): busca do plano de contas por código ou nome (o nome traduzido que aparece na tela). Fica o ramo que casa
 * inteiro (com as filhas, para ver o que está embaixo) e os pais de cada achado (o caminho até ele). Termo vazio: a árvore toda.
 *
 * Review r27 robustez (B2): devolve o MESMO objeto quando nada muda, para o `memo` do `Ramo` pular o ramo: o nó cujas filhas
 * ficaram todas é o próprio nó, a lista sem corte é a própria lista, e o pai podado que fica igual ao do termo anterior
 * (mesmas filhas) volta do `cache`. Antes, cada termo recriava todos os pais e milhares de ramos renderizavam de novo. O
 * texto comparável de cada nó (sem acento, minúsculo) também fica no `cache` (o `normalize` de 3 textos por nó, a cada termo).
 */
export interface CachePoda<T extends object> {
  textos: WeakMap<T, string[]>;
  podados: WeakMap<T, T>;
}
export const novoCachePoda = <T extends object>(): CachePoda<T> => ({ textos: new WeakMap(), podados: new WeakMap() });
const mesmos = <T,>(a: readonly T[], b: readonly T[]) => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * Review r27 robustez (B2): a árvore só é podada pelo texto a partir de 2 caracteres (1 casa com quase tudo e só custa).
 * Review r28 spec (O3): com 1 caractere vale o CÓDIGO EXATO ("3" acha "3 Despesas operacionais" e o ramo dele), para o
 * link `?busca=3` e o código de 1 dígito funcionarem; antes, a URL guardava o termo e a árvore ficava inteira.
 */
export const MIN_BUSCA_PLANO = 2;

/**
 * Review r29 spec (B1): 1 caractere que não é o código de nenhuma categoria do plano ("A", primeira tecla de "Aluguel").
 * A tela mantém a árvore inteira e diz "Digite 2 letras ou o código", em vez de "Nenhuma categoria com "A"" (falso: há
 * Aluguel). O código de 1 caractere ("3") segue achando o ramo.
 */
export function buscaCurtaSemCodigo(itens: readonly { codigo: string | null }[], termo: string): boolean {
  const alvo = comparavel(termo.trim());
  if (!alvo || alvo.length >= MIN_BUSCA_PLANO) return false;
  return !itens.some((c) => comparavel(c.codigo ?? '') === alvo);
}

export function podarArvore<T extends { codigo: string | null; nome: string; filhas: T[] }>(
  raizes: T[],
  termo: string,
  rotulo: (nome: string) => string = (n) => n,
  cache: CachePoda<T> = novoCachePoda<T>(),
): T[] {
  const alvo = comparavel(termo.trim());
  if (!alvo) return raizes;
  const textos = (n: T) => {
    let t = cache.textos.get(n);
    if (!t) cache.textos.set(n, (t = [comparavel(n.codigo ?? ''), comparavel(rotulo(n.nome)), comparavel(n.nome)]));
    return t;
  };
  const curto = alvo.length < MIN_BUSCA_PLANO;
  const casa = (n: T) => (curto ? textos(n)[0] === alvo : textos(n).some((t) => t.includes(alvo)));
  const podar = (nos: T[]): T[] => {
    const saida: T[] = [];
    for (const n of nos) {
      if (casa(n)) {
        saida.push(n);
        continue;
      }
      const filhas = podar(n.filhas);
      if (!filhas.length) continue;
      if (mesmos(filhas, n.filhas)) {
        saida.push(n);
        continue;
      }
      const antes = cache.podados.get(n);
      if (antes && mesmos(antes.filhas, filhas)) {
        saida.push(antes);
        continue;
      }
      const novo = { ...n, filhas };
      cache.podados.set(n, novo);
      saida.push(novo);
    }
    return saida;
  };
  const podadas = podar(raizes);
  return mesmos(podadas, raizes) ? raizes : podadas;
}

interface Edicao {
  categoria?: Categoria;
  tipo: TipoCategoria;
  paiId?: string | null;
}

interface AcoesRamo {
  aoNovaFilha?: (no: Categoria) => void;
  aoEditar?: (no: Categoria) => void;
  /** Review r29 visual (M2): o botão vai junto, para o foco voltar ao da mesma posição depois de excluir ou mover. */
  aoRemover?: (no: Categoria, botao: HTMLElement) => void;
  /** Fase 2, W7 (P2): mover os lançamentos para outra categoria e arquivar esta. */
  aoMover?: (no: Categoria, botao: HTMLElement) => void;
}

/**
 * Botões da linha (nova filha, editar, mover e arquivar, excluir), os mesmos no Quadro, na Árvore e na Lista (B5), com as
 * marcas que o foco usa depois de excluir ou mover (`data-excluir-categoria`, `data-mover-categoria`).
 */
function BotoesCategoria({ no, acoes }: { no: Categoria; acoes: AcoesRamo }) {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  return (
    <>
      {acoes.aoNovaFilha && (
        <button type="button" aria-label={`${t('acoes.novaFilha')}: ${rotuloCategoria(no.nome)}`} title={t('acoes.novaFilha')} onClick={() => acoes.aoNovaFilha?.(no)} className="rounded p-1 text-tinta-3 hover:text-tinta-1">
          <Plus className="size-4" />
        </button>
      )}
      {acoes.aoEditar && (
        <button type="button" aria-label={`${tn('comum.editar')}: ${rotuloCategoria(no.nome)}`} title={tn('comum.editar')} onClick={() => acoes.aoEditar?.(no)} className="rounded p-1 text-tinta-3 hover:text-tinta-1">
          <Pencil className="size-4" />
        </button>
      )}
      {acoes.aoMover && no.ativa && (
        <button type="button" aria-label={`${t('dre.mover')}: ${rotuloCategoria(no.nome)}`} title={t('dre.mover')} onClick={(e) => acoes.aoMover?.(no, e.currentTarget)} data-mover-categoria="" className="rounded p-1 text-tinta-3 hover:text-tinta-1">
          <Archive className="size-4" />
        </button>
      )}
      {acoes.aoRemover && (
        <button type="button" aria-label={`${tn('comum.excluir')}: ${rotuloCategoria(no.nome)}`} title={tn('comum.excluir')} onClick={(e) => acoes.aoRemover?.(no, e.currentTarget)} data-excluir-categoria="" className="rounded p-1 text-tinta-3 hover:text-perigo">
          <Trash2 className="size-4" />
        </button>
      )}
    </>
  );
}

/**
 * Ramo da árvore (r7 visual M3): fora de `Categorias`. Definido dentro dele, era um componente novo a cada render e a árvore
 * inteira remontava ao abrir ou fechar o modal; o botão de origem sumia e o foco caía no body.
 *
 * Review r26 robustez (B2): `memo`. Com 1.557 categorias, cada tecla da busca re-renderizava a árvore inteira (11 teclas,
 * 12 tarefas longas e 4 s de CPU; com 3.657, 10 s). Agora o ramo só renderiza de novo quando o nó dele muda (a árvore e a
 * poda são memorizadas) ou quando as ações mudam (permissões).
 */
const Ramo = memo(function Ramo({ no, nivel, acoes }: { no: No; nivel: number; acoes: AcoesRamo }) {
  const { t } = useTranslation('financeiro');
  // Review r35 (P2 a 1280): na grade de duas colunas, selo, grupo e lançamentos não cabiam juntos e a linha passava do
  // cartão; agora o selo quebra, grupo e lançamentos truncam, e o nome guarda um mínimo. Review r36 robustez (M6): o
  // mínimo (80 px) só do xl em diante; com 4 níveis, os 96 px do nome empurravam as ações para fora a 390 e a 1024.
  // Review r36 visual (M1): o selo do papel quebra entre palavras, mas não fica mais estreito que a maior delas
  // (`min-w-min`); antes o texto vazava do selo e cobria o botão de nova filha de 1024 a 1280. Na grade de duas colunas (lg,
  // 1024 a 1279) o grupo do DRE e os lançamentos saem da linha (espremiam o nome a 20 px); o nome disputa 96 px (base).
  return (
    <li>
      <div className="flex min-w-0 items-center gap-2 py-1.5 text-sm" style={{ paddingLeft: `${nivel * 1.25}rem` }}>
        {nivel > 0 && <CornerDownRight className="size-3.5 shrink-0 text-tinta-3" aria-hidden />}
        {no.codigo && <span className="numero shrink-0 text-xs text-tinta-3">{no.codigo}</span>}
        <span className={`min-w-0 grow basis-24 truncate xl:min-w-20 ${no.ativa ? '' : 'text-tinta-3 line-through'}`} title={rotuloCategoria(no.nome)}>{rotuloCategoria(no.nome)}</span>
        {!no.ativa && <Selo>{t('estado.inativa')}</Selo>}
        {no.sintetica && <Selo cor="roxo">{t('w24.selo')}</Selo>}
        {no.papel && (
          <span className="hidden min-w-min sm:inline-flex">
            <Selo cor="ciano" quebra>{t(`dre.papeis.${no.papel}`)}</Selo>
          </span>
        )}
        {/* Só o grupo gravado: a filha sem grupo herda o do pai, que aparece na linha dele (o efetivo está no formulário). */}
        {no.grupo_dre && <span className="hidden min-w-0 truncate text-xs text-tinta-2 md:inline lg:hidden xl:inline" title={t(`dre.grupos.${no.grupo_dre}`)}>{t(`dre.grupos.${no.grupo_dre}`)}</span>}
        {no.lancamentos > 0 && <span className="numero hidden min-w-0 truncate text-xs text-tinta-3 sm:inline lg:hidden xl:inline" title={t('categorias.lancamentos', { count: no.lancamentos })}>{t('categorias.lancamentos', { count: no.lancamentos })}</span>}
        <span className="flex shrink-0 gap-0.5">
          <BotoesCategoria no={no} acoes={acoes} />
        </span>
      </div>
      {no.filhas.length > 0 && (
        <ul>
          {no.filhas.map((f) => (
            <Ramo key={f.id} no={f} nivel={nivel + 1} acoes={acoes} />
          ))}
        </ul>
      )}
    </li>
  );
});

/** Classes da tabela do núcleo (`componentes/tabela.tsx`), para a Árvore e a Lista terem a mesma cara das listas. */
const TH = 'px-4 py-2.5 font-medium whitespace-nowrap text-tinta-3';
const TD = 'px-4 py-2';

/** Natureza, grupo da DRE e status de uma categoria, nas colunas da Árvore e da Lista (o efetivo, herdado do pai). */
function useColunasCategoria() {
  const { t } = useTranslation('financeiro');
  return {
    tipo: (c: Categoria) => t(`dre.naturezas.${c.tipo}`),
    natureza: (c: Categoria) => {
      const n = c.natureza ?? c.natureza_efetiva;
      return n ? t(`dre.naturezas.${n}`) : '';
    },
    grupo: (c: Categoria) => {
      const g = c.grupo_dre ?? c.grupo_dre_efetivo;
      return g ? t(`dre.grupos.${g}`) : '';
    },
    status: (c: Categoria) => (
      <span className="flex flex-wrap gap-1">
        {c.ativa ? <Selo cor="verde">{t('categorias.ativa')}</Selo> : <Selo>{t('estado.inativa')}</Selo>}
        {c.sintetica && <Selo cor="roxo">{t('w24.selo')}</Selo>}
      </span>
    ),
  };
}

/**
 * B5 · modo Árvore (como no antigo): um cartão com receitas e despesas juntas, cabeçalho de colunas, seta para abrir e fechar
 * cada ramo (começa recolhida), "Expandir tudo" e "Recolher tudo". Na busca, os ramos com o termo ficam abertos.
 */
function ArvorePlano({ raizes, buscando, acoes }: { raizes: No[]; buscando: boolean; acoes: AcoesRamo }) {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const col = useColunasCategoria();
  const [abertos, setAbertos] = useState<Set<string>>(() => new Set());
  const linhas = useMemo(() => linhasDaArvore(raizes, abertos, buscando), [raizes, abertos, buscando]);
  const alternar = (id: string) =>
    setAbertos((a) => {
      const n = new Set(a);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  return (
    <Cartao
      className="lg:col-span-2"
      semPadding
      titulo={t('categorias.planoContas')}
      acao={
        <span className="flex flex-wrap gap-1">
          <Botao tamanho="sm" variante="fantasma" icone={<ChevronsUpDown className="size-4" />} disabled={buscando} onClick={() => setAbertos(idsComFilhas(raizes))} data-expandir-tudo="">
            {t('categorias.expandirTudo')}
          </Botao>
          <Botao tamanho="sm" variante="fantasma" icone={<ChevronsDownUp className="size-4" />} disabled={buscando} onClick={() => setAbertos(new Set())} data-recolher-tudo="">
            {t('categorias.recolherTudo')}
          </Botao>
        </span>
      }
    >
      <div className="relative overflow-x-auto" tabIndex={0} role="region" aria-label={t('categorias.planoContas')}>
        <table className="w-full text-sm" data-plano-arvore="">
          <thead>
            <tr className="border-b border-borda text-left">
              <th scope="col" className={TH}>{t('campos.codigo')}</th>
              <th scope="col" className={TH}>{t('campos.nome')}</th>
              <th scope="col" className={TH}>{t('campos.tipo')}</th>
              <th scope="col" className={TH}>{t('dre.natureza')}</th>
              <th scope="col" className={TH}>{t('dre.grupo')}</th>
              <th scope="col" className={`${TH} text-right`}>{t('categorias.colLancamentos')}</th>
              <th scope="col" className={TH}>{t('categorias.status')}</th>
              <th scope="col" className={TH}><span className="sr-only">{tn('comum.acoes')}</span></th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(({ no, nivel }, i) => {
              const aberto = buscando || abertos.has(no.id);
              const nome = rotuloCategoria(no.nome);
              return (
                <tr key={no.id} className={i === 0 ? '' : 'border-t border-borda'} data-nivel={nivel}>
                  <td className={`${TD} numero whitespace-nowrap text-tinta-3`}>{no.codigo}</td>
                  <td className={TD}>
                    <span className="flex min-w-48 items-center gap-1" style={{ paddingLeft: `${nivel * 1}rem` }}>
                      {no.filhas.length > 0 ? (
                        <button type="button" aria-expanded={aberto} aria-label={t(aberto ? 'categorias.fecharRamo' : 'categorias.abrirRamo', { nome })} disabled={buscando} onClick={() => alternar(no.id)} className="rounded p-0.5 text-tinta-3 hover:text-tinta-1" data-abrir-ramo="">
                          {aberto ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                        </button>
                      ) : (
                        <span className="inline-block size-5" aria-hidden />
                      )}
                      <span className={`${nivel === 0 ? 'font-semibold' : ''} ${no.ativa ? '' : 'text-tinta-3 line-through'}`}>{nome}</span>
                      {no.papel && <Selo cor="ciano">{t(`dre.papeis.${no.papel}`)}</Selo>}
                    </span>
                  </td>
                  <td className={`${TD} whitespace-nowrap`}>{col.tipo(no)}</td>
                  <td className={`${TD} whitespace-nowrap`}>{col.natureza(no)}</td>
                  <td className={TD}>{col.grupo(no)}</td>
                  <td className={`${TD} numero text-right`}>{no.lancamentos}</td>
                  <td className={TD}>{col.status(no)}</td>
                  <td className={TD}>
                    <span className="flex justify-end gap-0.5">
                      <BotoesCategoria no={no} acoes={acoes} />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Cartao>
  );
}

type OrdemLista = 'codigo' | 'nome' | 'lancamentos';

/** B5: ordem da Lista pela coluna clicada (código em ordem natural, nome no idioma, lançamentos em número). */
export function ordenarLista<C extends Categoria>(itens: C[], ordem: OrdemLista, direcao: 'asc' | 'desc'): C[] {
  const cmp = ordem === 'codigo' ? porCodigo : ordem === 'nome' ? (a: C, b: C) => rotuloCategoria(a.nome).localeCompare(rotuloCategoria(b.nome)) : (a: C, b: C) => a.lancamentos - b.lancamentos || porCodigo(a, b);
  const lista = [...itens].sort(cmp);
  return direcao === 'asc' ? lista : lista.reverse();
}

/**
 * B5 · modo Lista (como no antigo): plana, receitas e despesas juntas, com a categoria mãe (o caminho), ordenável pelo código,
 * pelo nome e pelos lançamentos. Os dados são os já carregados (`useCategorias`), então não há paginação no servidor.
 */
function ListaPlano({ itens, todas, acoes }: { itens: Categoria[]; todas: Categoria[]; acoes: AcoesRamo }) {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const col = useColunasCategoria();
  const [ordem, setOrdem] = useState<{ ordem: OrdemLista; direcao: 'asc' | 'desc' }>({ ordem: 'codigo', direcao: 'asc' });
  const porId = useMemo(() => new Map(todas.map((c) => [c.id, c])), [todas]);
  const linhas = useMemo(() => ordenarLista(itens, ordem.ordem, ordem.direcao), [itens, ordem]);
  const cabeca = (chave: OrdemLista, rotulo: string, direita = false) => (
    <th scope="col" className={`${TH} ${direita ? 'text-right' : ''}`} aria-sort={ordem.ordem === chave ? (ordem.direcao === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button type="button" onClick={() => setOrdem((o) => ({ ordem: chave, direcao: o.ordem === chave && o.direcao === 'asc' ? 'desc' : 'asc' }))} className="inline-flex items-center gap-1 hover:text-tinta-1">
        {rotulo}
        {ordem.ordem === chave && (ordem.direcao === 'asc' ? <ArrowUp className="size-3.5" /> : <ArrowDown className="size-3.5" />)}
      </button>
    </th>
  );
  return (
    <Cartao className="lg:col-span-2" semPadding titulo={t('categorias.planoContas')}>
      <div className="relative overflow-x-auto" tabIndex={0} role="region" aria-label={t('categorias.planoContas')}>
        <table className="w-full text-sm" data-plano-lista="">
          <thead>
            <tr className="border-b border-borda text-left">
              {cabeca('codigo', t('campos.codigo'))}
              {cabeca('nome', t('campos.nome'))}
              <th scope="col" className={TH}>{t('categorias.categoriaMae')}</th>
              <th scope="col" className={TH}>{t('campos.tipo')}</th>
              <th scope="col" className={TH}>{t('dre.natureza')}</th>
              <th scope="col" className={TH}>{t('dre.grupo')}</th>
              <th scope="col" className={TH}>{t('w24.sintetica')}</th>
              {cabeca('lancamentos', t('categorias.colLancamentos'), true)}
              <th scope="col" className={TH}>{t('categorias.status')}</th>
              <th scope="col" className={TH}><span className="sr-only">{tn('comum.acoes')}</span></th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((c, i) => {
              const pai = c.parent_id ? porId.get(c.parent_id) : undefined;
              return (
                <tr key={c.id} className={i === 0 ? '' : 'border-t border-borda'}>
                  <td className={`${TD} numero whitespace-nowrap text-tinta-3`}>{c.codigo}</td>
                  <td className={TD}>
                    <span className={`min-w-40 ${c.ativa ? '' : 'text-tinta-3 line-through'}`}>{rotuloCategoria(c.nome)}</span>
                  </td>
                  <td className={`${TD} text-tinta-2`}>{pai ? caminhoDaCategoria(pai, todas) : ''}</td>
                  <td className={`${TD} whitespace-nowrap`}>{col.tipo(c)}</td>
                  <td className={`${TD} whitespace-nowrap`}>{col.natureza(c)}</td>
                  <td className={TD}>{col.grupo(c)}</td>
                  <td className={`${TD} whitespace-nowrap`}>{c.sintetica ? t('w24.selo') : t('categorias.analitica')}</td>
                  <td className={`${TD} numero text-right`}>{c.lancamentos}</td>
                  <td className={TD}>{col.status(c)}</td>
                  <td className={TD}>
                    <span className="flex justify-end gap-0.5">
                      <BotoesCategoria no={c} acoes={acoes} />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Cartao>
  );
}

const SELETOR_EXCLUIR = '[data-excluir-categoria]';
const SELETOR_MOVER = '[data-mover-categoria]';
const SELETOR_BUSCA_PLANO = '[data-busca-plano] input';

/** M4.2: árvore de categorias, receitas de um lado e despesas do outro. */
export function Categorias() {
  const { t } = useTranslation('financeiro');
  const { t: tn, i18n } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useCategorias();
  const [edicao, setEdicao] = useState<Edicao | null>(null);
  const [remover, setRemover] = useState<Categoria | null>(null);
  const [importar, setImportar] = useState(false);
  const [mover, setMover] = useState<Categoria | null>(null);
  const [destino, setDestino] = useState('');
  // Review r29 visual (M2): posição do botão que abriu o Excluir ou o Mover (a linha some ou perde o Mover depois).
  const posicao = useRef(-1);
  const itens = useMemo(() => q.data?.itens ?? [], [q.data]);
  // Review r25 spec (O3): a busca vai para a URL (`?busca=`, replace), como nas outras listas (r24 spec B4): o link filtra e
  // a URL mostra o que está na tela. Outro link com outra busca, com a página aberta, troca o termo.
  // Review r26 robustez (B2): a tecla só muda o campo; a URL e a árvore seguem o termo JÁ ESPERADO (`termoD`, 200 ms).
  const [url, setUrl] = useSearchParams();
  const [termo, setTermo] = useState(() => buscaDaUrl(url));
  const termoD = useDebounce(termo, 200);
  const buscaDoMomento = useBuscaDoMomento();
  // O que esta tela gravou por último na URL: a volta dele pelo `useSearchParams` não apaga o que foi digitado depois.
  const gravada = useRef(buscaDaUrl(url));
  useEffect(() => {
    const valor = termoD.trim() ? termoD : '';
    if (valor === buscaDaUrl(buscaDoMomento())) return;
    gravada.current = valor;
    setUrl(comBuscaNaUrl(buscaDoMomento(), termoD), { replace: true });
  }, [termoD, setUrl, buscaDoMomento]);
  const buscaUrl = buscaDaUrl(url);
  useEffect(() => {
    if (buscaUrl === gravada.current) return;
    gravada.current = buscaUrl;
    setTermo((b) => ((b.trim() ? b : '') === buscaUrl ? b : buscaUrl));
  }, [buscaUrl]);
  // Review r26 robustez (B2): as árvores só mudam com as categorias (e o idioma, do rótulo); a poda, com o termo esperado.
  const arvores = useMemo(() => ({ receita: montarArvore(itens, 'receita'), despesa: montarArvore(itens, 'despesa') }), [itens]);
  // B5: modo de exibição (Quadro, o de hoje, padrão; Árvore e Lista como no antigo), na URL e lembrado no aparelho.
  const [visao, mudarVisao] = useVisaoPlano();
  const arvoreUnica = useMemo(() => (visao === 'quadro' ? [] : (montarArvoreUnica(itens) as unknown as No[])), [itens, visao]);
  // Review r27 robustez (B2): a poda segue o termo esperado ADIADO (`useDeferredValue`): a árvore renderiza em segundo plano,
  // interrompível pela próxima tecla, e a digitação no ritmo comum (200 a 300 ms) não espera milhares de ramos. A memória da
  // poda (pais iguais e textos comparáveis) vale até o idioma mudar o rótulo.
  // Review r28 spec (O3): com 1 caractere a poda vale pelo código exato (`podarArvore`), então a árvore segue o que está na
  // URL (`?busca=3`); antes, 1 caractere ia para a URL e a árvore ficava inteira.
  const termoArvore = useDeferredValue(termoD.trim());
  // eslint-disable-next-line react-hooks/exhaustive-deps -- o idioma muda o rótulo traduzido que a busca compara.
  const cachePoda = useMemo(() => novoCachePoda<No>(), [i18n.language]);
  // Review r29 spec (B1): 1 caractere que não é código de nenhuma categoria mantém a árvore inteira, com a dica.
  const curtoSemCodigo = useMemo(() => buscaCurtaSemCodigo(itens, termoArvore), [itens, termoArvore]);
  const podadas = useMemo(
    () =>
      curtoSemCodigo
        ? arvores
        : { receita: podarArvore(arvores.receita, termoArvore, rotuloCategoria, cachePoda), despesa: podarArvore(arvores.despesa, termoArvore, rotuloCategoria, cachePoda) },
    [arvores, termoArvore, cachePoda, curtoSemCodigo],
  );
  // B5: a mesma busca nos três modos (a Árvore e a Lista podam a árvore única).
  const buscandoUnica = !curtoSemCodigo && !!termoArvore;
  const unicaPodada = useMemo(() => (buscandoUnica ? podarArvore(arvoreUnica, termoArvore, rotuloCategoria, cachePoda) : arvoreUnica), [arvoreUnica, termoArvore, cachePoda, buscandoUnica]);
  const itensDaLista = useMemo(() => (visao === 'lista' ? nosDaArvore(unicaPodada) : []), [unicaPodada, visao]);
  // Fase 2, W24 (K9): sugestão do próximo código pelo pai escolhido (a mesma função do servidor, `@malgoos/shared`).
  const [paiNoForm, setPaiNoForm] = useState<string | null>(null);
  const sugestaoDoPai = (paiId: string | null | undefined) => {
    const pai = paiId ? itens.find((c) => c.id === paiId) : null;
    if (paiId && !pai) return null;
    return sugerirCodigoHierarquico(pai ? (pai.codigo ?? '') : null, itens.map((c) => c.codigo));
  };
  const sugestao = edicao && !edicao.categoria ? sugestaoDoPai(paiNoForm) : null;
  const abrirNova = (e: Edicao) => {
    setPaiNoForm(e.paiId ?? null);
    setEdicao(e);
  };

  const excluir = useMutation({
    mutationFn: (c: Categoria) => del(`/financeiro/categorias/${c.id}`),
    onSuccess: async () => {
      await invalidarFinanceiro(qc);
      setRemover(null);
      toast.sucesso(tn('comum.removido'));
      recuperarFoco({ seletor: SELETOR_EXCLUIR, i: posicao.current, reservas: [SELETOR_BUSCA_PLANO] });
    },
    onError: (e) => {
      setRemover(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  const moverMut = useMutation({
    mutationFn: (v: { id: string; destino_id: string }) => post<{ lancamentos: number }>(`/financeiro/categorias/${v.id}/mover-e-arquivar`, { destino_id: v.destino_id }),
    onSuccess: async (r) => {
      await invalidarFinanceiro(qc);
      setMover(null);
      setDestino('');
      recuperarFoco({ seletor: SELETOR_MOVER, i: posicao.current, reservas: [SELETOR_BUSCA_PLANO] });
      toast.sucesso(t('dre.movido', { count: r.lancamentos }));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const campos: DefCampo[] = edicao
    ? [
        { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true, largura: 'meia' },
        { nome: 'codigo', rotulo: t('campos.codigo'), tipo: 'texto', largura: 'meia', ajuda: sugestao ? t('w24.sugestaoCodigo', { codigo: sugestao }) : t('ajuda.codigo') },
        {
          nome: 'parent_id',
          rotulo: t('campos.categoriaPai'),
          tipo: 'selecao',
          ajuda: t('ajuda.categoriaPai'),
          opcoes: itens.filter((c) => c.tipo === edicao.tipo && c.id !== edicao.categoria?.id).map((c) => ({ valor: c.id, rotulo: caminhoDaCategoria(c, itens) })),
        },
        { nome: 'ativa', rotulo: t('campos.ativa'), tipo: 'booleano', largura: 'meia' },
        // Fase 2, W24 (K9): sintética só agrupa; analítica recebe lançamento.
        { nome: 'sintetica', rotulo: t('w24.sintetica'), tipo: 'booleano', largura: 'meia', ajuda: t('w24.ajudaSintetica') },
        // Fase 2, W7 (E-4): natureza e grupo da DRE explícitos (vazio herda do pai) e o uso automático pela baixa.
        {
          nome: 'grupo_dre',
          rotulo: t('dre.grupo'),
          tipo: 'selecao',
          largura: 'meia',
          placeholder: t('dre.herdar'),
          ajuda: edicao.categoria?.grupo_dre_efetivo && !edicao.categoria.grupo_dre ? t('dre.herdado', { valor: t(`dre.grupos.${edicao.categoria.grupo_dre_efetivo}`) }) : t('dre.ajudaGrupo'),
          opcoes: GRUPOS_DRE.map((g) => ({ valor: g, rotulo: t(`dre.grupos.${g}`) })),
        },
        { nome: 'natureza', rotulo: t('dre.natureza'), tipo: 'selecao', largura: 'meia', placeholder: t('dre.herdar'), ajuda: t('dre.ajudaNatureza'), opcoes: NATUREZAS.map((n) => ({ valor: n, rotulo: t(`dre.naturezas.${n}`) })) },
        { nome: 'papel', rotulo: t('dre.papel'), tipo: 'selecao', placeholder: t('dre.nenhum'), ajuda: t('dre.ajudaPapel'), opcoes: PAPEIS_DO_TIPO[edicao.tipo].map((p) => ({ valor: p, rotulo: t(`dre.papeis.${p}`) })) },
      ]
    : [];
  // Review r26 robustez (B2): ações estáveis (só as permissões as trocam), para o `memo` do ramo valer.
  const [podeCriar, podeEditar, podeExcluir] = [s.pode('financeiro.criar'), s.pode('financeiro.editar'), s.pode('financeiro.excluir')];
  const acoes = useMemo<AcoesRamo>(
    () => ({
      aoNovaFilha: podeCriar
        ? (no) => {
            setPaiNoForm(no.id);
            setEdicao({ tipo: no.tipo, paiId: no.id });
          }
        : undefined,
      aoEditar: podeEditar ? (no) => setEdicao({ categoria: no, tipo: no.tipo }) : undefined,
      aoRemover: podeExcluir
        ? (no, botao) => {
            posicao.current = posicaoNaPagina(SELETOR_EXCLUIR, botao);
            setRemover(no);
          }
        : undefined,
      aoMover: podeEditar
        ? (no, botao) => {
            posicao.current = posicaoNaPagina(SELETOR_MOVER, botao);
            setDestino('');
            setMover(no);
          }
        : undefined,
    }),
    [podeCriar, podeEditar, podeExcluir],
  );
  const opcoesDestino = mover ? itens.filter((c) => c.tipo === mover.tipo && c.ativa && !c.sintetica && c.id !== mover.id).map((c) => ({ valor: c.id, rotulo: caminhoDaCategoria(c, itens) })) : [];

  return (
    <>
      {s.pode('financeiro.criar') && (
        <AcaoPrimaria>
          {s.pode('financeiro.excluir') && <BotaoLixeira entidades={['financeiro.categoria']} />}
          {/* Val5 (29/10): importar pede `financeiro.importar`. */}
          {s.pode('financeiro.importar') && s.pode('financeiro.criar') && s.pode('financeiro.editar') && (
            <>
              <BotaoModeloCsv caminho="/financeiro/categorias" />
              <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
                {t('acoes.importarCategorias')}
              </Botao>
            </>
          )}
          <Botao icone={<Plus className="size-4" />} onClick={() => abrirNova({ tipo: 'despesa' })}>
            {t('acoes.novaDespesa')}
          </Botao>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => abrirNova({ tipo: 'receita' })}>
            {t('acoes.novaReceita')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Estado consulta={q} vazio={(d) => d.itens.length === 0}>
        {() => (
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Review r26 robustez (O7): acima do teto da consulta (50 páginas de 200), a árvore diz que foi cortada. */}
            {q.data?.proximo_cursor && <p className="text-sm text-tinta-3 lg:col-span-2">{t('categorias.cortada', { count: itens.length, n: numero(itens.length) })}</p>}
            <div className="lg:col-span-2" data-visao-plano="">
              <Segmentado rotulo={t('categorias.visao')} valor={visao} opcoes={VISOES_PLANO.map((v) => ({ valor: v, rotulo: t(`categorias.modos.${v}`) }))} aoMudar={mudarVisao} />
            </div>
            <div className="relative min-w-0 lg:col-span-2" data-busca-plano="">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
              <Entrada type="search" aria-label={t('categorias.buscar')} placeholder={t('categorias.buscar')} className="pl-9" value={termo} onChange={(e) => setTermo(e.target.value)} />
            </div>
            {curtoSemCodigo && (
              <p className="text-sm text-tinta-3 lg:col-span-2" role="status" data-busca-curta="">
                {t('categorias.digiteMais')}
              </p>
            )}
            {visao !== 'quadro' && buscandoUnica && unicaPodada.length === 0 && (
              <p className="text-sm text-tinta-3 lg:col-span-2">{t('categorias.semResultado', { termo: termoArvore })}</p>
            )}
            {visao === 'arvore' && unicaPodada.length > 0 && <ArvorePlano raizes={unicaPodada} buscando={buscandoUnica} acoes={acoes} />}
            {visao === 'lista' && itensDaLista.length > 0 && <ListaPlano itens={itensDaLista} todas={itens} acoes={acoes} />}
            {visao === 'quadro' && (['receita', 'despesa'] as const).map((tipo) => {
              const todas = arvores[tipo];
              const raizes = podadas[tipo];
              return (
                <Cartao key={tipo} titulo={t(tipo === 'receita' ? 'categorias.receitas' : 'categorias.despesas')}>
                  {raizes.length === 0 ? (
                    <p className="text-sm text-tinta-3">{todas.length && termoArvore.trim() ? t('categorias.semResultado', { termo: termoArvore.trim() }) : t('vazio.categoriasTipo')}</p>
                  ) : (
                    <ul className="divide-y divide-borda">
                      {raizes.map((r) => (
                        <Ramo key={r.id} no={r} nivel={0} acoes={acoes} />
                      ))}
                    </ul>
                  )}
                </Cartao>
              );
            })}
          </div>
        )}
      </Estado>
      <Modal
        aberto={!!edicao}
        aoFechar={() => setEdicao(null)}
        titulo={edicao?.categoria ? edicao.categoria.nome : t(edicao?.tipo === 'receita' ? 'acoes.novaReceita' : 'acoes.novaDespesa')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setEdicao(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-categoria">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        {edicao && (
          <Formulario
            key={edicao.categoria?.id ?? `nova-${edicao.tipo}-${edicao.paiId ?? ''}`}
            id="form-categoria"
            campos={campos}
            inicial={edicao.categoria ? { ...edicao.categoria } : { ativa: true, sintetica: false, parent_id: edicao.paiId ?? null, codigo: sugestaoDoPai(edicao.paiId) ?? '' }}
            aoMudar={(v) => setPaiNoForm((v.parent_id as string | null) || null)}
            aoEnviar={async (v) => {
              const corpo = {
                nome: v.nome,
                tipo: edicao.tipo,
                codigo: v.codigo || null,
                parent_id: v.parent_id || null,
                ativa: !!v.ativa,
                grupo_dre: v.grupo_dre || null,
                natureza: v.natureza || null,
                papel: v.papel || null,
                sintetica: !!v.sintetica,
              };
              if (edicao.categoria) await patch(`/financeiro/categorias/${edicao.categoria.id}`, corpo);
              else await post('/financeiro/categorias', corpo);
              await invalidarFinanceiro(qc);
              toast.sucesso(tn('comum.salvo'));
              setEdicao(null);
            }}
          />
        )}
      </Modal>
      <Modal
        aberto={!!mover}
        aoFechar={() => setMover(null)}
        titulo={mover ? t('dre.moverTitulo', { nome: mover.nome }) : ''}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setMover(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" disabled={!destino} carregando={moverMut.isPending} onClick={() => mover && destino && moverMut.mutate({ id: mover.id, destino_id: destino })}>
              {t('dre.mover')}
            </Botao>
          </>
        }
      >
        <p className="mb-4 text-sm text-tinta-2">{t('dre.moverTexto')}</p>
        <Rotulo id="destino-categoria" rotulo={t('dre.destino')} obrigatorio>
          <Selecao id="destino-categoria" opcoes={opcoesDestino} vazio={tn('comum.selecione')} value={destino} onChange={(e) => setDestino(e.target.value)} />
        </Rotulo>
      </Modal>
      <Confirmar
        aberto={!!remover}
        aoFechar={() => setRemover(null)}
        aoConfirmar={() => remover && excluir.mutate(remover)}
        carregando={excluir.isPending}
        perigoso
        titulo={t('categorias.removerTitulo')}
        texto={t('categorias.removerTexto')}
      />
      <DialogoImportacao aberto={importar} aoFechar={() => setImportar(false)} caminho="/financeiro/categorias" titulo={t('acoes.importarCategorias')} aoConcluir={() => void invalidarFinanceiro(qc)} />
    </>
  );
}
